import classNames from 'classnames';
import type { FC } from 'react';
import React from 'react';
import type { LinkViewModel } from '../Link/LinkViewModel.csharp';
import { List } from '../List/List';
import { Shortcut } from '../Shortcut/Shortcut';
import type { ShortcutSize } from '../Shortcut/ShortcutSize';
import type { ShortcutVariant } from '../Shortcut/ShortcutVariant';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import { AdditionalNotes } from '~/Partials/LinkListSection/Components/AdditionalNotes/AdditionalNotes';
import type { Theme } from '~/Utils/Theme';
import styles from './LinkList.module.css';

interface LinkListItem extends LinkViewModel {
    notes?: string[];
}

interface LinkListProps {
    links: LinkListItem[];
    linkVariant: ShortcutVariant;
    linkSize?: ShortcutSize;
    className?: string;
    itemClassName?: string;
    itemWrapperClassName?: string;
    theme?: Theme;
}

export const LinkList: FC<React.PropsWithChildren<LinkListProps>> = props => {
    const contextualTheme = useTheme();
    const {
        links,
        linkVariant,
        className,
        itemClassName,
        itemWrapperClassName,
        linkSize,
        theme = contextualTheme,
    } = props;
    const { withBoxShadow, withRoundedCorners } = theme;

    const renderItem = (link: LinkListItem, index: number) => {
        const hasNotes = link.notes && link.notes.length !== 0;

        return (
            <li key={index} className={itemClassName}>
                <div className={classNames(itemWrapperClassName, hasNotes && styles['LinkWrapper--withNotes'])}>
                    <Shortcut {...link} type="outline" variant={linkVariant} size={linkSize} className={styles.Link} />
                    {hasNotes && <AdditionalNotes notes={link.notes} />}
                </div>
            </li>
        );
    };

    return (
        <List
            className={classNames(
                styles.LinkList,
                withBoxShadow && styles['Variant--shadowed'],
                withRoundedCorners && styles['Variant--rounded'],
                className
            )}
            items={links}
            renderItem={renderItem}
        />
    );
};
